import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { TemplateDefinitionOverrides } from '../resolveCatalogueTemplate';
import type { CatalogueTemplateRegion, LayoutOverrides, TemplateSlotList } from '../schema';

export interface TemplateDraftEntry {
  key: string;
  template: string;
  design: string;
  overrides: TemplateDefinitionOverrides;
  updatedAt: string;
  savedAt: string | null;
  isDirty: boolean;
}

export interface TemplateDraftDockState {
  isMinimized: boolean;
  lastDraftKey: string | null;
  lastTemplate: string | null;
  lastDesign: string | null;
}

export interface TemplateDraftState {
  entries: Record<string, TemplateDraftEntry>;
  dock: TemplateDraftDockState;
}

const initialState: TemplateDraftState = {
  entries: {},
  dock: {
    isMinimized: false,
    lastDraftKey: null,
    lastTemplate: null,
    lastDesign: null,
  },
};

interface UpsertDraftPayload {
  key: string;
  template: string;
  design: string;
  slots?: Record<string, TemplateSlotList> | null;
  regions?: CatalogueTemplateRegion[] | null;
  layout?: Record<string, LayoutOverrides> | null;
}

interface MarkSavedPayload {
  key: string;
  timestamp?: string;
}

interface ResetDraftPayload {
  key: string;
}

interface SetActiveDraftPayload {
  key: string | null;
  template?: string | null;
  design?: string | null;
}

interface SetDockMinimizedPayload {
  minimized: boolean;
  key?: string | null;
  template?: string | null;
  design?: string | null;
}

const cloneLayoutOverride = (value: LayoutOverrides): LayoutOverrides => {
  const cloned: LayoutOverrides = {};

  if (value.className !== undefined) {
    cloned.className = value.className;
  }
  if (value.fullWidth !== undefined) {
    cloned.fullWidth = value.fullWidth;
  }
  if (value.ratio !== undefined) {
    cloned.ratio = value.ratio;
  }
  if (value.style) {
    cloned.style = { ...value.style };
  }
  if (value.tokens) {
    cloned.tokens = { ...value.tokens };
  }
  if (value.grid) {
    cloned.grid = { ...value.grid };
  }

  return cloned;
};

const cloneLayoutRecord = (
  layout: Record<string, LayoutOverrides>,
): Record<string, LayoutOverrides> => {
  const cloned: Record<string, LayoutOverrides> = {};
  Object.entries(layout).forEach(([key, value]) => {
    cloned[key] = cloneLayoutOverride(value);
  });
  return cloned;
};

const templateDraftSlice = createSlice({
  name: 'templateDrafts',
  initialState,
  reducers: {
    upsertTemplateDraft(state, action: PayloadAction<UpsertDraftPayload>) {
      const { key, template, design, slots, regions, layout } = action.payload;
      const now = new Date().toISOString();
      const previous = state.entries[key];
      const previousOverrides = previous?.overrides ?? {};
      const nextOverrides: TemplateDefinitionOverrides = { ...previousOverrides };

      if (typeof slots !== 'undefined') {
        if (slots) {
          nextOverrides.slots = slots;
        } else {
          delete nextOverrides.slots;
        }
      }

      if (typeof regions !== 'undefined') {
        if (regions) {
          nextOverrides.regions = [...regions];
        } else {
          delete nextOverrides.regions;
        }
      }

      if (typeof layout !== 'undefined') {
        if (layout && Object.keys(layout).length > 0) {
          nextOverrides.layout = cloneLayoutRecord(layout);
        } else {
          delete nextOverrides.layout;
        }
      }

      state.entries[key] = {
        key,
        template,
        design,
        overrides: nextOverrides,
        updatedAt: now,
        savedAt: previous?.savedAt ?? null,
        isDirty: true,
      };
    },
    markTemplateDraftSaved(state, action: PayloadAction<MarkSavedPayload>) {
      const { key, timestamp } = action.payload;
      const entry = state.entries[key];
      if (!entry) {
        return;
      }
      entry.savedAt = timestamp ?? new Date().toISOString();
      entry.isDirty = false;
    },
    resetTemplateDraft(state, action: PayloadAction<ResetDraftPayload>) {
      const { key } = action.payload;
      delete state.entries[key];
      if (state.dock.lastDraftKey === key) {
        state.dock.lastDraftKey = null;
        state.dock.lastTemplate = null;
        state.dock.lastDesign = null;
        state.dock.isMinimized = false;
      }
    },
    clearTemplateDrafts(state) {
      state.entries = {};
      state.dock = { ...initialState.dock };
    },
    setActiveTemplateDraft(state, action: PayloadAction<SetActiveDraftPayload>) {
      const { key, template = null, design = null } = action.payload;
      const normalizedKey = key ?? null;
      if (state.dock.lastDraftKey !== normalizedKey) {
        state.dock.isMinimized = false;
      }
      state.dock.lastDraftKey = normalizedKey;
      state.dock.lastTemplate = template;
      state.dock.lastDesign = design;
    },
    setTemplateDraftDockMinimized(state, action: PayloadAction<SetDockMinimizedPayload>) {
      const { minimized, key, template, design } = action.payload;
      state.dock.isMinimized = minimized;
      if (typeof key !== 'undefined') {
        state.dock.lastDraftKey = key ?? null;
      }
      if (typeof template !== 'undefined') {
        state.dock.lastTemplate = template ?? null;
      }
      if (typeof design !== 'undefined') {
        state.dock.lastDesign = design ?? null;
      }
    },
  },
});

export const {
  upsertTemplateDraft,
  markTemplateDraftSaved,
  resetTemplateDraft,
  clearTemplateDrafts,
  setActiveTemplateDraft,
  setTemplateDraftDockMinimized,
} = templateDraftSlice.actions;

export default templateDraftSlice.reducer;

export const selectTemplateDraftEntry = (
  state: { templateDrafts: TemplateDraftState },
  key: string,
): TemplateDraftEntry | undefined => state.templateDrafts.entries[key];

export const selectTemplateDrafts = (state: { templateDrafts: TemplateDraftState }) =>
  state.templateDrafts.entries;

export const selectTemplateDraftDock = (state: { templateDrafts: TemplateDraftState }) =>
  state.templateDrafts.dock;
